import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiSearch, FiChevronDown, FiX } from 'react-icons/fi';
import { colors, spacing, borderRadius, typography } from '../theme/colors';
import { focusNext } from '../utils/focusNext';
import { getAccountsCached } from '../utils/masterDataStore';

const AccountSelect = ({ companyId, value, onChange, label, excludePrefixes, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // Dropdown is portaled to <body> so it escapes ancestor `overflow: auto/hidden`
  // (e.g. the line-item table containers on JV / receipts / payments). We track
  // the trigger's bounding rect and render the dropdown at fixed coordinates.
  const [dropdownRect, setDropdownRect] = useState(null);

  // Load all Level 5 (leaf) accounts
  const loadAccounts = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Tier 1 (Firebase cost): device-local cached accounts (0 reads/mount).
      // A Journal Voucher renders one AccountSelect per line — this used to be
      // a full accounts read per line.
      const all = await getAccountsCached(companyId);
      let accs = all
        .filter(a => a.level === 5 && a.isGroup !== true && a.isActive !== false);

      // Exclude specified prefixes (e.g. cash/bank accounts)
      if (excludePrefixes && excludePrefixes.length > 0) {
        accs = accs.filter(a => !excludePrefixes.some(prefix => a.code?.startsWith(prefix)));
      }

      accs.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
      setAccounts(accs);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, excludePrefixes]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const selectedAccount = useMemo(() =>
    accounts.find(a => a.id === value) || null,
    [accounts, value]
  );

  const filteredAccounts = useMemo(() => {
    if (!search.trim()) return accounts;
    const q = search.toLowerCase();
    return accounts.filter(a =>
      (a.code || '').toLowerCase().includes(q) ||
      (a.name || '').toLowerCase().includes(q)
    );
  }, [accounts, search]);

  // Outside-click also has to ignore clicks INSIDE the portaled dropdown,
  // since it sits in <body> and isn't a descendant of `wrapperRef`.
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideTrigger = wrapperRef.current && wrapperRef.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recompute the portaled dropdown position whenever the trigger moves
  // (scroll, resize, window resize). Falls back to closing if the trigger
  // goes off-screen mid-edit (very rare).
  useEffect(() => {
    if (!isOpen) { setDropdownRect(null); return; }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true); // capture: catches scrolls in any ancestor
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  const handleSelect = (account, { advanceFocus = false } = {}) => {
    onChange({
      id: account.id,
      code: account.code,
      name: account.name,
      // Party link (customer/vendor) — lets callers (e.g. the Journal Voucher)
      // detect a party control account and offer invoice settlement. Extra
      // fields; existing callers that read only id/code/name are unaffected.
      linkedEntityId: account.linkedEntityId || null,
      linkedEntityType: account.linkedEntityType || null
    });
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(0);
    if (advanceFocus) {
      setTimeout(() => { if (triggerRef.current) focusNext(triggerRef.current); }, 30);
    } else {
      setTimeout(() => triggerRef.current?.focus(), 30);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ id: '', code: '', name: '' });
    setSearch('');
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  useEffect(() => { setHighlightedIndex(0); }, [search]);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen && selectedAccount && e.key === 'Enter') {
      e.preventDefault();
      focusNext(triggerRef.current);
      return;
    }
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      handleOpen();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredAccounts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const a = filteredAccounts[highlightedIndex];
      if (a) handleSelect(a, { advanceFocus: true });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearch('');
      setTimeout(() => triggerRef.current?.focus(), 30);
    } else if (e.key === 'Tab') {
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <SelectTrigger
        ref={triggerRef}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        $disabled={disabled}
        $hasValue={!!selectedAccount}
      >
        {isOpen ? (
          <SearchInputWrapper>
            <FiSearch size={14} />
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by account code or name..."
              onClick={e => e.stopPropagation()}
            />
          </SearchInputWrapper>
        ) : (
          <TriggerText $hasValue={!!selectedAccount}>
            {selectedAccount ? `${selectedAccount.code} - ${selectedAccount.name}` : `-- Select ${label || 'Account'} --`}
          </TriggerText>
        )}
        <TriggerIcons>
          {selectedAccount && !disabled && <ClearBtn onClick={handleClear}><FiX size={14} /></ClearBtn>}
          <FiChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        </TriggerIcons>
      </SelectTrigger>

      {isOpen && dropdownRect && createPortal(
        <Dropdown
          ref={dropdownRef}
          style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
        >
          {loading ? (
            <NoResults>Loading accounts...</NoResults>
          ) : filteredAccounts.length === 0 ? (
            <NoResults>No accounts found</NoResults>
          ) : (
            filteredAccounts.map((a, idx) => (
              <DropdownItem
                key={a.id}
                onClick={() => handleSelect(a, { advanceFocus: true })}
                onMouseEnter={() => setHighlightedIndex(idx)}
                $selected={a.id === value}
                $highlighted={idx === highlightedIndex}
              >
                <AccCode>{a.code}</AccCode>
                <AccName>{a.name}</AccName>
              </DropdownItem>
            )))}
        </Dropdown>,
        document.body
      )}
    </Wrapper>
  );
};

// ===== STYLED =====
const Wrapper = styled.div`position:relative;`;
const SelectTrigger = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:${spacing.sm} ${spacing.md};
  border:1px solid ${props => props.$hasValue ? colors.primary.main + '60' : colors.border.light};
  border-radius:${borderRadius.sm};font-size:${typography.fontSize.sm};
  color:${colors.text.primary};background:${props => props.$disabled ? colors.neutral[100] : colors.background.default};
  cursor:${props => props.$disabled ? 'not-allowed' : 'pointer'};
  min-height:36px;transition:border-color 0.2s, box-shadow 0.2s;
  &:hover{border-color:${props => props.$disabled ? colors.border.light : colors.primary.main};}
  &:focus{outline:none;border-color:${colors.primary.main};box-shadow:0 0 0 2px ${colors.primary.main}30;}
`;
const TriggerText = styled.span`color:${props => props.$hasValue ? colors.text.primary : colors.text.tertiary};`;
const TriggerIcons = styled.div`display:flex;align-items:center;gap:4px;color:${colors.text.secondary};`;
const ClearBtn = styled.button`
  display:flex;align-items:center;justify-content:center;
  width:20px;height:20px;background:${colors.neutral[200]};border:none;
  border-radius:50%;cursor:pointer;color:${colors.text.secondary};
  &:hover{background:${colors.error.main};color:white;}
`;
const SearchInputWrapper = styled.div`display:flex;align-items:center;gap:${spacing.sm};flex:1;color:${colors.text.secondary};`;
const SearchInput = styled.input`
  border:none;outline:none;background:none;flex:1;
  font-size:${typography.fontSize.sm};color:${colors.text.primary};
  &::placeholder{color:${colors.text.tertiary};}
`;
const Dropdown = styled.div`
  /* Position is set inline by the parent — see useEffect that tracks
     the trigger's getBoundingClientRect. position: fixed lets us escape
     ancestor overflow:hidden / overflow:auto clipping containers. */
  position:fixed;z-index:1000;
  max-height:280px;overflow-y:auto;
  background:${colors.background.default};
  border:1px solid ${colors.border.light};
  border-radius:${borderRadius.sm};
  box-shadow:0 6px 16px rgba(0,0,0,0.18);
`;
const DropdownItem = styled.div`
  display:flex;align-items:center;gap:${spacing.sm};
  padding:8px ${spacing.md};cursor:pointer;
  background:${props => props.$highlighted ? colors.primary.main + '15' : props.$selected ? colors.primary.main + '10' : 'transparent'};
  border-left:${props => props.$selected ? `3px solid ${colors.primary.main}` : '3px solid transparent'};
  &:hover{background:${colors.neutral[50]};}
  &:not(:last-child){border-bottom:1px solid ${colors.neutral[100]};}
`;
const AccCode = styled.span`
  font-family:monospace;font-size:12px;color:${colors.primary.main};
  background:${colors.primary.main}10;padding:2px 6px;border-radius:4px;
  white-space:nowrap;
`;
const AccName = styled.span`font-size:${typography.fontSize.sm};color:${colors.text.primary};flex:1;`;
const NoResults = styled.div`padding:${spacing.md};text-align:center;color:${colors.text.tertiary};font-size:${typography.fontSize.sm};`;

export default AccountSelect;
